// src/pages/productsseller/Step7MainFileUpload.tsx

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { AiOutlineCloudUpload, AiOutlineClose } from "react-icons/ai";

interface FileType {
  id: string; // 고유 식별자 추가
  name: string;
  size: number; // KB 단위
  progress: number;
  status: 'uploading' | 'completed';
  isThumbnail: boolean;
  preview: string;
}

interface Step7MainFileUploadProps {
  mainFile: FileType | null;
  handleMainFileChange: (file: File) => Promise<void>;
  handleRemoveMainFile: () => void; // 메인 파일 제거 함수 추가
  errors: { [key: string]: string };
}

const Step7MainFileUpload: React.FC<Step7MainFileUploadProps> = ({
                                                                   mainFile,
                                                                   handleMainFileChange,
                                                                   handleRemoveMainFile,
                                                                   errors,
                                                                 }) => {

  // react-dropzone 훅 사용
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleMainFileChange(acceptedFiles[0]); // 단일 파일 업로드
      }
    },
    [handleMainFileChange]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'], // ZIP 파일만 허용
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: false, // 단일 파일 업로드
    noClick: true, // 클릭 이벤트 비활성화
    noKeyboard: true, // 키보드 이벤트 비활성화
  });

  return (
    <div className="w-full px-4">
      <label htmlFor="mainFile" className="block font-medium mb-1">
        메인 파일 업로드 <span className="text-red-500">*</span>
      </label>
      <p className="text-gray-500 text-sm mb-2">
        상품의 메인 파일을 업로드 해주세요. (ZIP 포맷, 최대 100MB)
      </p>

      {/* 드래그 앤 드롭 섹션 */}
      <div
        {...getRootProps()}
        className={`mt-4 border-dashed border-2 rounded-md p-4 cursor-pointer ${
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
        }`}
      >
        <input {...getInputProps()} id="mainFile" />
        <div className="flex justify-center items-center flex-col">
          <p className="text-gray-600 text-center mt-2">
            {isDragActive ? '파일을 놓아주세요...' : '파일을 선택하거나 여기에 드래그 앤 드롭하세요.'}
          </p>
          <p className="text-sm text-gray-400 mb-2">ZIP 포맷, 최대 100MB.</p>
          <button
            type="button"
            className="bg-gray-100 px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-200"
            onClick={(e) => {
              e.stopPropagation(); // Dropzone 트리거 방지
              open(); // 파일 선택 창 열기
            }}
          >
            파일 선택
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {errors.mainFile && (
        <p className="text-red-500 text-sm mt-1">{errors.mainFile}</p>
      )}

      {/* 메인 파일 표시 */}
      {mainFile && (
        <div className="mt-4 flex items-center justify-between p-2 border-b border-gray-200">
          <div className="flex items-center">
            {/* ZIP 파일 아이콘 표시 */}
            <AiOutlineCloudUpload size={40} className="text-gray-500 mr-3" />
            <div>
              <p className="font-medium text-gray-700">{mainFile.name}</p>
              <p className="text-sm text-gray-500">{mainFile.size} KB</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* 업로드 진행 상태 */}
            {mainFile.status === 'uploading' && (
              <>
                <p className="text-sm text-gray-500">{mainFile.progress}%</p>
                <div className="w-24 bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${mainFile.progress}%` }}
                  ></div>
                </div>
              </>
            )}
            {mainFile.status === 'completed' && (
              <p className="text-sm text-green-600">업로드 완료</p>
            )}

            {/* 파일 제거 버튼 */}
            <button
              type="button"
              className="text-red-500 hover:text-red-700"
              onClick={handleRemoveMainFile} // handleRemoveMainFile 호출
            >
              <AiOutlineClose size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step7MainFileUpload;
